import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { fabricApi } from "../../imports";
import { getImageIds } from "../../helpers/fabricHelpers";
import Card from "./Card";

/**
 * Individual product recommendation cards
 * Handles logic for rendering customer images using mediamodifier
 *
 * @param {Object}  product   Current product for card
 * @param {Object}  file      Current customer uploaded image
 * @param {Object}  mockup    Product's mediamodifier mockup information
 *
 * @returns {Object}
 */
export default function ProductCard({ product, file, mockup }) {
  const [imagesrc, setImagesrc] = useState(product.featured_image);
  const { imageIds, currentNode, imageWidth, imageHeight } = useSelector(
    (state) => ({
      imageIds: getImageIds(state),
      currentNode: state.playerState.currentNode,
      imageHeight: state.playerState.finalObj.imageHeightOriginal,
      imageWidth: state.playerState.finalObj.imageWidthOriginal,
    })
  );

  // On uploaded image height and width change
  // useEffect(() => {
  //   if (file !== null) {
  //     getMockup();
  //   }
  // }, [imageHeight, imageWidth]);

  // Attach image id to product url for preloading
  const getHref = () => {
    let params = imageIds;
    const productUrl = "/products/" + product.handle;

    /**
     * Multiple image products have multiple imageids
     * keyed with the node name. In this case we only
     * want to send along the current node image as a
     * single imageId
     */
    if (currentNode) {
      const string = imageIds[currentNode];
      params = { imageId: string };
    }

    const queryString = new URLSearchParams(params).toString();

    // If there is an image id query, add to url
    return params["imageId"] === ""
      ? productUrl
      : productUrl + "?" + queryString;
  };

  /**
   * Start of process by fetching the mockup from mediamodifier
   * https://docs.mediamodifier.com/docs/mockup-api/ee69f403d950a-get-single-mockup
   */
  async function getMockup() {
    // If there is no mockup record created, skip this
    if (mockup === null) {
      return;
    }

    // Get mockup
    const returnedMockup = await fabricApi.fetchProductMockup(mockup.id);
    const mockupLayers = returnedMockup.mockup.layers;

    // Find image layer, get width and height
    const imageLayer = mockupLayers.find((layer) => layer.id === mockup.layer);
    const { height, width } = imageLayer.placeholder;

    // Get base64 version of image
    getBase64Data(width, height);
  }

  /**
   * Mediamodifier requires image in base64 format
   * Resize image with html canvas
   */
  function getBase64Data(layerWidth, layerHeight) {
    // File reader allows us to load a file as an image
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = function (e) {
      // Create image and load in file
      const image = new Image();
      image.src = e.target.result;
      image.onload = function () {
        // Create canvas
        const canvas = document.createElement("canvas");

        // New dimensions based on size ratio
        const heightRatio = layerHeight / image.height;
        const widthRatio = layerWidth / image.width;
        const ratio = heightRatio > widthRatio ? heightRatio : widthRatio;
        const newWidth = image.width * ratio;
        const newHeight = image.height * ratio;

        // Set canvas width
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext("2d");

        // Draw image onto canvas at smaller size
        ctx.drawImage(image, 0, 0, newWidth, newHeight);

        // Convert canvas to base64
        const base64 = canvas.toDataURL("image/png");

        // Get dimensions to center image
        const xChange = (newWidth - layerWidth) / 2;
        const yChange = (newHeight - layerHeight) / 2;

        // Create crop object for render
        const layerCrop = {
          x: xChange,
          y: yChange,
          width: layerWidth,
          height: layerHeight,
        };

        // Render the mockup
        renderMockup(base64, layerCrop);
      };
    };
  }

  /**
   * Fetch and set the final rendered mockup
   * https://docs.mediamodifier.com/docs/mockup-api/fef6bf4bb36f0-render-mockup
   */
  async function renderMockup(base64, layerCrop) {
    const payload = {
      id: mockup.id,
      layer_inputs: [
        {
          id: mockup.layer,
          data: base64,
          checked: true,
          crop: layerCrop,
        },
      ],
    };
    const returnedImage = await fabricApi.fetchProductMockupRender(payload);
    setImagesrc(returnedImage.base64);
  }

  return (
    <Card key={product.id} product={product} img={imagesrc} href={getHref()} />
  );
}
